// src/pages/tournament/PlayersPanel.jsx
import React, { useRef, useState } from "react";
import { useConfirm } from "../../components/ConfirmDialog";
import {
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UploadIcon,
} from "../../components/icons";
import PreviousPlayers from "./PreviousPlayers";

// How the player list can be ordered. "added" keeps the order the server sends (the order players were added).
const SORTS = {
  added: { label: "Order added", compare: null },
  name: {
    label: "Name (A–Z)",
    compare: (a, b) =>
      a.lastName.localeCompare(b.lastName) ||
      a.firstName.localeCompare(b.firstName),
  },
  ranking: {
    label: "Ranking (best first)",
    compare: (a, b) => (a.ranking ?? Infinity) - (b.ranking ?? Infinity),
  },
  played: {
    label: "Games played (most first)",
    compare: (a, b) => (b.playedMatches ?? 0) - (a.playedMatches ?? 0),
  },
  breaks: {
    label: "Breaks (most first)",
    compare: (a, b) => (b.breaks ?? 0) - (a.breaks ?? 0),
  },
};

// What the organizer can say about a player before there are results. Only the pairings use it (not the ranking),
// and less and less as the player plays games.
const LEVELS = [
  ["beginner", "Beginner"],
  ["casual", "Casual"],
  ["good", "Intermediate"],
  ["strong", "Advanced"],
  ["very_strong", "League 4"],
  ["league_3", "League 3"],
  ["league_2", "League 2"],
  ["league_1", "League 1"],
  ["nlb", "NLB"],
  ["nla", "NLA"],
];

function LevelSelect({ value, onChange, label }) {
  return (
    <select
      className="input level-select"
      aria-label={label}
      title="Used for the pairings only, never for the ranking, and less and less as games are played"
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Level: not set</option>
      {LEVELS.map(([key, name]) => (
        <option key={key} value={key}>
          {name}
        </option>
      ))}
    </select>
  );
}

const FILTERS = {
  all: { label: "All players", test: () => true },
  active: { label: "Active", test: (p) => p.active !== false },
  inactive: { label: "Inactive", test: (p) => p.active === false },
  unplayed: { label: "Not played yet", test: (p) => !(p.playedMatches > 0) },
  played: { label: "Played", test: (p) => p.playedMatches > 0 },
  nolevel: { label: "No level set", test: (p) => !p.startLevel },
  haslevel: { label: "Level set", test: (p) => !!p.startLevel },
};

function sortPlayers(players, sort) {
  const { compare } = SORTS[sort];
  if (!compare) return players;
  const byName = SORTS.name.compare;
  return [...players].sort((a, b) => compare(a, b) || byName(a, b));
}

function PlayersPanel({
  players,
  loading,
  onAdd,
  onSetActive,
  onSetLevel,
  onRename,
  onDelete,
  onUpload,
  searchPrevious,
  onShowDetail,
  locked,
}) {
  const confirm = useConfirm();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [level, setLevel] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("added");
  const [filter, setFilter] = useState("all");
  const [renaming, setRenaming] = useState(null); // { playerId, firstName, lastName }
  const firstNameRef = useRef(null);
  const fileRef = useRef(null);
  const inactiveCount = players.filter(
    (player) => player.active === false
  ).length;
  const filtered = players.filter(FILTERS[filter].test);
  const matching = query.trim()
    ? filtered.filter((player) =>
        `${player.firstName} ${player.lastName}`
          .toLowerCase()
          .includes(query.trim().toLowerCase())
      )
    : filtered;
  const shownPlayers = sortPlayers(matching, sort);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    const player = await onAdd(firstName.trim(), lastName.trim(), level);
    if (player) {
      setFirstName("");
      setLastName("");
      firstNameRef.current?.focus(); // ready for the next player
    }
  };

  const handleDelete = async (player) => {
    const confirmed = await confirm({
      title: `Delete ${player.firstName} ${player.lastName}?`,
      message:
        "The player will be removed from this tournament, including the ranking. To keep them in the ranking, deactivate them instead.",
      confirmLabel: "Delete player",
    });
    if (confirmed) onDelete(player.playerId);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    const added = await onUpload(file);
    setUploading(false);
    if (added) {
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <section className="card players-card" aria-labelledby="players-heading">
      <div className="card-header">
        <h2 id="players-heading">Players</h2>
        <span className="badge badge-primary">
          {players.length - inactiveCount}
        </span>
        {inactiveCount > 0 && (
          <span className="badge">{inactiveCount} inactive</span>
        )}
      </div>

      <div className="card-body">
        {locked ? (
          <p className="hint">
            This tournament is finished, so players can no longer be added or
            changed.
          </p>
        ) : (
          <>
            <h3 className="subheading">Add player</h3>
            <form className="player-form" onSubmit={handleSubmit}>
              <div className="field-row">
                <input
                  ref={firstNameRef}
                  className="input"
                  aria-label="First name"
                  placeholder="First name"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                />
                <input
                  className="input"
                  aria-label="Last name"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                />
              </div>
              <LevelSelect
                value={level}
                onChange={setLevel}
                label="Level of the new player"
              />
              <button
                className="btn btn-primary"
                type="submit"
                disabled={!firstName.trim() || !lastName.trim()}
              >
                <PlusIcon /> Add player
              </button>
            </form>

            {searchPrevious && (
              <PreviousPlayers
                search={searchPrevious}
                onAdd={onAdd}
                refreshKey={players.length}
              />
            )}

            <div className="upload">
              <div className="upload-row">
                <UploadIcon size={16} />
                <input
                  id="players-file"
                  ref={fileRef}
                  className="sr-only"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(event) => setFile(event.target.files[0] || null)}
                />
                <label htmlFor="players-file" className="btn btn-sm">
                  Choose Excel file
                </label>
                <span className="spacer" />
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                >
                  Upload
                </button>
              </div>
              <p className="hint">
                {file
                  ? file.name
                  : "Needs first and last name columns; an optional level column (Beginner to NLA) sets the starting levels."}
              </p>
            </div>
          </>
        )}

        <h3 className="subheading">Player list</h3>
        {!loading && players.length > 0 && (
          <div className="player-tools">
            <input
              className="input player-search"
              type="search"
              aria-label="Search players"
              placeholder="Search players"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <select
              className="input player-sort"
              aria-label="Filter players"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            >
              {Object.entries(FILTERS).map(([key, { label, test }]) => (
                <option key={key} value={key}>
                  {label} ({players.filter(test).length})
                </option>
              ))}
            </select>
            <select
              className="input player-sort"
              aria-label="Sort players"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
            >
              {Object.entries(SORTS).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <div className="skeleton" aria-busy="true" />
        ) : players.length === 0 ? (
          <div className="empty">
            <strong>No players yet</strong>
            <p>Add players above or upload an Excel file.</p>
          </div>
        ) : shownPlayers.length === 0 ? (
          <p className="hint">
            No players match
            {query.trim() ? ` "${query.trim()}"` : ""}
            {filter !== "all" ? ` in "${FILTERS[filter].label}"` : ""}.
          </p>
        ) : (
          <ul className="player-list">
            {shownPlayers.map((player) => {
              const inactive = player.active === false;
              return (
                <li
                  key={player.playerId}
                  className={`player-row${inactive ? " inactive" : ""}`}
                >
                  <span className="player-rank" title="Ranking">
                    {player.ranking ?? "–"}
                  </span>
                  <div className="player-info">
                    {onShowDetail ? (
                      <button
                        type="button"
                        className="player-name player-name-link"
                        onClick={() => onShowDetail(player)}
                      >
                        {player.firstName} {player.lastName}
                        {inactive && <span className="badge">Inactive</span>}
                      </button>
                    ) : (
                      <div className="player-name">
                        {player.firstName} {player.lastName}
                        {inactive && <span className="badge">Inactive</span>}
                      </div>
                    )}
                    {renaming?.playerId === player.playerId && (
                      <form
                        className="player-rename"
                        onSubmit={async (event) => {
                          event.preventDefault();
                          if (
                            await onRename(
                              player.playerId,
                              renaming.firstName,
                              renaming.lastName
                            )
                          ) {
                            setRenaming(null);
                          }
                        }}
                      >
                        <input
                          className="input"
                          aria-label="Corrected first name"
                          autoFocus
                          value={renaming.firstName}
                          onChange={(event) =>
                            setRenaming({
                              ...renaming,
                              firstName: event.target.value,
                            })
                          }
                        />
                        <input
                          className="input"
                          aria-label="Corrected last name"
                          value={renaming.lastName}
                          onChange={(event) =>
                            setRenaming({
                              ...renaming,
                              lastName: event.target.value,
                            })
                          }
                        />
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setRenaming(null)}
                        >
                          Cancel
                        </button>
                      </form>
                    )}
                    <div className="player-stats">
                      <span>Played {player.playedMatches ?? 0}</span>
                      <span title="Rounds this player sat out">
                        Breaks {player.breaks ?? 0}
                      </span>
                      {player.singles > 0 && (
                        <span title="Singles games played">
                          Singles {player.singles}
                        </span>
                      )}
                      <span title="Times this player faced the same opponent again">
                        Repeat opponents {player.duplicateOpponents ?? 0}
                      </span>
                      <span title="Times this player was paired with the same teammate again">
                        Repeat teammates {player.duplicateTeammates ?? 0}
                      </span>
                    </div>
                    {!locked && (
                      <div className="player-controls">
                        {onSetLevel && (
                          <LevelSelect
                            value={player.startLevel}
                            onChange={(value) =>
                              onSetLevel(player.playerId, value)
                            }
                            label={`Level of ${player.firstName} ${player.lastName}`}
                          />
                        )}
                        <button
                          className="btn btn-ghost btn-sm player-toggle"
                          title={
                            inactive
                              ? "Include this player in new rounds again"
                              : "Leave this player out of new rounds. They stay in the list and the ranking."
                          }
                          aria-label={`${
                            inactive ? "Activate" : "Deactivate"
                          } ${player.firstName} ${player.lastName}`}
                          onClick={() => onSetActive(player.playerId, inactive)}
                        >
                          {inactive ? "Activate" : "Deactivate"}
                        </button>
                      </div>
                    )}
                  </div>
                  {!locked && onRename && (
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      aria-label={`Rename ${player.firstName} ${player.lastName}`}
                      onClick={() =>
                        setRenaming({
                          playerId: player.playerId,
                          firstName: player.firstName,
                          lastName: player.lastName,
                        })
                      }
                    >
                      <PencilIcon size={16} />
                    </button>
                  )}
                  {!locked && (
                    <button
                      className="btn btn-ghost btn-icon btn-sm btn-icon-danger"
                      aria-label={`Delete ${player.firstName} ${player.lastName}`}
                      onClick={() => handleDelete(player)}
                    >
                      <TrashIcon size={16} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

export default PlayersPanel;
